import {
    AfterViewInit,
    Component, ComponentFactoryResolver,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild, ViewContainerRef
} from '@angular/core';
import {
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    ValidationErrors,
    Validators
} from '@angular/forms';
import {
    FormDefinitionFieldSubType,
    FormDefinitionFieldType
} from '../../../../modules/form/definition/field/api/entity/formDefinitionFieldSummary.model';
import {
    FormDefinitionFieldFindOneByIdService
} from '../../../../modules/form/definition/field/impl/service/formDefinitionFieldFindOneById.service';
import {FormDefinitionFieldModel} from '../../../../modules/form/definition/field/api/entity/formDefinitionField.model';
import {BehaviorSubject, Observable, Subscription} from 'rxjs';
import {SelectFilterElement} from '../../select-filter/select-filter.model';
import {Focussable} from '../../focussable';
import {FormValueModel} from '../../../../modules/form/form/value/api/entity/formValue.model';
import {
    FormFormSaveComponentService, FormFormSaveComponentServiceFormValue
} from '../../../../modules/form/form/api/component/form-form-save/form-form-save-component.service';
import {FieldInputTypeHandlerRegister} from './type-handler/IFieldInputTypeHandler';
import {DialogController} from "../../../../modules/common/dialog/dialogController";
import {
    FieldInputCommentDialogComponent,
    FieldInputCommentDialogInput
} from "../field-input-comment-dialog/field-input-comment-dialog.component";
import {FieldDefinitionUtils} from "./fieldDefinition.utils";
import {PartnerSummaryModel} from "../../../../modules/partner/common/api/model/partnerSummary.model";
import {SelectElementV2} from "../../select/selectElementV2";
import {ChipItem} from "../../../../modules/core/component/chip/chip.component";


@Component({
    selector: 'app-field-input',
    templateUrl: './field-input.component.html',
    styleUrls: ['./field-input.component.scss']
})
export class FieldInputComponent implements OnChanges, Focussable, OnInit, OnDestroy, AfterViewInit {

    @ViewChild('custom', {read: ViewContainerRef}) container: ViewContainerRef;
    @Input() displayType: 'default' | 'compact' = 'default';
    @Input() measured = false;
    @Input() parentFormGroup: UntypedFormGroup;
    @Input('formValue') inputFormValue: FormValueModel;
    @Input() componentService: FormFormSaveComponentService;
    @Input() size = undefined;
    @Input() label: string;
    @Input() sizeXl = undefined;
    @Input() sizeLg = undefined;
    @Input() partner: PartnerSummaryModel;

    @ViewChild('input') inputRef: Focussable;
    FormDefinitionFieldType = FormDefinitionFieldType;
    subscriptions: Subscription[] = [];
    inputChangedSubscription: Subscription;
    FormDefinitionFieldSubType = FormDefinitionFieldSubType;
    options:  SelectElementV2<any>[] = [];
    chipItems: ChipItem<string> [] = [];

    constructor(private formBuilder: UntypedFormBuilder,
                private componentFactoryResolver: ComponentFactoryResolver,
                private dialogController: DialogController) {
    }

    ngOnInit(): void {
        if (this.componentService) {
            this.subscriptions.push(
                this.componentService.changedFormValue$.subscribe((value: FormFormSaveComponentServiceFormValue) => {
                    if (value?.fieldDefinitionId === this.inputFormValue?.fieldDefinition?.summary?.id) {
                        this.inputFormValue.value = value.value;
                        const measured = !!value.value;
                        this.parentFormGroup.patchValue({
                            value: value.value,
                            values: [],
                            ruleValue: value.valueForRule,
                            measured
                        });
                    }
                }));
        }
    }

    ngOnDestroy(): void {
        this.subscriptions?.forEach(value => value.unsubscribe());
        if (this.inputChangedSubscription) {
            this.inputChangedSubscription.unsubscribe();
        }
    }

    get computedSize(): number {
        // TODO could be improved
        if (this.formDefinitionFieldModel?.summary?.type?.value === FormDefinitionFieldType.FORM_DEFINITION_FIELD_TYPE_JSON
            || this.formDefinitionFieldModel?.summary?.type?.value === FormDefinitionFieldType.FORM_DEFINITION_FIELD_TYPE_EMBEDDED_FILE
            || this.formDefinitionFieldModel?.summary?.type?.value === FormDefinitionFieldType.FORM_DEFINITION_FIELD_TYPE_DOCUMENT
            || this.formDefinitionFieldModel?.summary?.type?.value === FormDefinitionFieldType.FORM_DEFINITION_FIELD_TYPE_RICH_TEXT
            || this.formDefinitionFieldModel?.summary?.subType?.value === FormDefinitionFieldSubType.FORM_DEFINITION_FIELD_SUBTYPE_LONGTEXT) {
            return 12;
        }
        return this.size;
    }


    ngOnChanges(changes: SimpleChanges): void {
        if (changes['inputFormValue'] && changes['inputFormValue'].currentValue) {
            this.initFieldsWithFormDefinitionFieldModel();
            this.refreshComponent();
            this.options = !this.formDefinitionFieldModel.options ? [] : this.formDefinitionFieldModel.options.map(value => new SelectElementV2<string>(value.value, value.label, value.value));
            this.chipItems = !this.formDefinitionFieldModel.options ? [] : this.formDefinitionFieldModel.options.map(value => ChipItem.createChipItem(value.value, value.label));

            if (this.inputFormValue.value && this.chipItems?.length > 0) {
                const chipItem = this.chipItems.find(value => value.value === this.inputFormValue.value);
                if (chipItem) {
                    chipItem.selected = true;
                }
            }
        }
        if (changes['measured']) {
            this.coreMeasured = this.measured;
        }
    }

    ngAfterViewInit() {
        this.refreshComponent();
    }

    refreshComponent() {
        if (this.container) {
            this.container?.clear();
            if (this.isCustom) {
                const typeValue = this.inputFormValue.fieldDefinition.summary.type.value;
                const subTyeValue = this.inputFormValue.fieldDefinition.summary.subType?.value;
                const impl = FieldInputTypeHandlerRegister.getImplementation(typeValue,
                    subTyeValue);
                const componentFactory = this.componentFactoryResolver.resolveComponentFactory(impl);
                const component = this.container.createComponent(componentFactory);
                component.instance.fieldFormGroup = this.parentFormGroup;
                component.instance.partnerSummary = this.partner;
                component.instance.formValue = this.inputFormValue;
            }
        }
    }

    get isCustom(): boolean {
        return FieldDefinitionUtils.isCustom(this.inputFormValue.fieldDefinition.summary.type.value, this.inputFormValue.fieldDefinition.summary.subType?.value);
    }

    onChipSelected() {
        this.coreMeasured = true;
        this.parentFormGroup.patchValue({
            value: this.chipItems.find(value => value.selected)?.value
        });
    }


    private initFieldsWithFormDefinitionFieldModel() {
        const formDefinitionField = this.inputFormValue.fieldDefinition;
        let value;
        let comment: string;
        if (this.inputFormValue.id) {
            value = this.inputFormValue.value;
            comment = this.inputFormValue.comment;
        } else {
            value = (this.inputFormValue.value || this.inputFormValue.value === false || this.inputFormValue.value === 0)
                ? this.inputFormValue.value
                : formDefinitionField.defaultValue;
        }

        const measured = !!this.inputFormValue.value;

        /* if (!this.formValue.id && !value && formDefinitionField.options?.length > 0) {
            value = formDefinitionField.options[0].value;
        } */

        let valueValidators: ValidationErrors;
        let valuesValidators: ValidationErrors;
        if (formDefinitionField.summary.subType?.value === FormDefinitionFieldSubType.FORM_DEFINITION_FIELD_SUBTYPE_SELECT_MANY) {
            valuesValidators = formDefinitionField.required ? Validators.required : null;
        } else {
            valueValidators = formDefinitionField.required ? Validators.required : null;
        }

        this.parentFormGroup.removeControl('id');
        this.parentFormGroup.removeControl('name');
        this.parentFormGroup.removeControl('value');
        this.parentFormGroup.removeControl('values');
        this.parentFormGroup.removeControl('measured');
        this.parentFormGroup.removeControl('comment')

        this.parentFormGroup.addControl('id', new UntypedFormControl(formDefinitionField.summary.id));
        this.parentFormGroup.addControl('name', new UntypedFormControl(formDefinitionField.summary.name));
        this.parentFormGroup.addControl('value', new UntypedFormControl({value, disabled: false}, valueValidators));
        this.parentFormGroup.addControl('values', new UntypedFormControl([], valuesValidators));
        this.parentFormGroup.addControl('ruleValue', new UntypedFormControl(undefined));
        this.parentFormGroup.addControl('measured', this.formBuilder.control(measured));
        this.parentFormGroup.addControl('comment', this.formBuilder.control(comment));

        if (this.inputChangedSubscription) {
            this.inputChangedSubscription.unsubscribe();
            this.inputChangedSubscription = undefined;
        }
        this.inputChangedSubscription = this.parentFormGroup.get('value').valueChanges.subscribe((value) => {
           if (!this.isCustom) {
                this.parentFormGroup.get('ruleValue').setValue(value);
            }
            if (this.componentService) {
                this.componentService.setChangedFromInput({
                    fieldDefinitionName: this.inputFormValue?.fieldDefinition?.summary?.name,
                    fieldDefinitionId: this.inputFormValue?.fieldDefinition?.summary?.id,
                    value,
                    valueForRule: this.parentFormGroup.get('ruleValue').value
                });
            }
        });

        this.coreMeasured  = measured;
    }


    focus() {
        this.inputRef.focus();
    }

    fieldId() {
        return this.parentFormGroup?.get('id')?.value;
    }

    valueChanged() {
        this.coreMeasured = true;
    }

    focussed() {
        this.coreMeasured = true;
    }

    openCommentDialog() {
        this.dialogController.open(FieldInputCommentDialogComponent, {
            comment: this.parentFormGroup.value.comment,
            label: this.label
        } as FieldInputCommentDialogInput, {
            title: 'Commentaire',
            width: '95%'
        }).afterClosed()
            .toPromise()
            .then(result => this.parentFormGroup.patchValue({
                comment: result
            }));
    }

    get coreMeasured(): boolean {
        return this.parentFormGroup.get('measured').value;
    }

    set coreMeasured(value: boolean) {
        if (this.inputFormValue){
            if (this.inputFormValue.fieldDefinition.notMeasuredAvailable === false){
               value = true;
            }
            this.parentFormGroup.get('measured').setValue(value);
            console.log('coreMeasured');
        }

    }
    get formDefinitionFieldModel(): FormDefinitionFieldModel {
        return this.inputFormValue?.fieldDefinition;
    }

     /*private extractDefaultValue(formDefinitionFieldModel: FormDefinitionFieldModel): boolean | number | string {
        switch (formDefinitionFieldModel.type.value) {
            case FormDefinitionFieldType.FORM_DEFINITION_FIELD_TYPE_YES_NO :
                return formDefinitionFieldModel.booleanDefaultValue ? formDefinitionFieldModel.booleanDefaultValue : false;
            case FormDefinitionFieldType.FORM_DEFINITION_FIELD_TYPE_NUMERIC :
                return formDefinitionFieldModel.numberDefaultValue ? formDefinitionFieldModel.numberDefaultValue : 0;
            case FormDefinitionFieldType.FORM_DEFINITION_FIELD_TYPE_TEXT :
            case FormDefinitionFieldType.FORM_DEFINITION_FIELD_TYPE_CHOICE :
                return formDefinitionFieldModel.textDefaultValue;
        }
        return null;
    } */
}
